/**
 * localizedRoutes.ts
 *
 * Central slug-mapping layer for all multilingual public informational pages
 * and the Useful section (hubs + articles).
 *
 * Usage pattern:
 *   getPagePath('about', 'ru')        → '/ru/o-zavode'
 *   getPagePath('about', 'en')        → '/en/about'
 *   buildUsefulHubPath('ru', 'spc-laminat')   → '/ru/poleznoe/spc-laminat'
 *   buildUsefulArticlePath('en', 'spc-laminat', 'kupit-cena') → '/en/useful/spc-laminate/buy-price'
 *
 * Reverse helpers:
 *   resolvePageKeyFromSlug('o-zavode', 'ru')       → 'about'
 *   resolveHubKeyFromSlug('poleznoe', 'ru')        → null  (poleznoe is the useful ROOT slug)
 *   resolveHubIdFromSlug('spc-paneli', 'ru')       → 'spc-paneli'  (canonical hub id)
 *   resolveArticleIdFromSlug('spc-paneli', 'kupit-cena', 'ru') → 'kupit-cena'
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type Lang = 'ru' | 'en' | 'uz';

// ─── Page slug map ────────────────────────────────────────────────────────────
// Keys are canonical page identifiers (English). Values are per-language slugs.

export const PAGE_SLUG_MAP: Record<string, Record<Lang, string>> = {
  about: { ru: 'o-zavode', en: 'about', uz: 'zavod-haqida' },
  news: { ru: 'novosti', en: 'news', uz: 'yangiliklar' },
  portfolio: { ru: 'portfolio', en: 'portfolio', uz: 'portfolio' },
  esg: { ru: 'esg', en: 'esg', uz: 'esg' },
  'where-to-buy': { ru: 'gde-kupit', en: 'where-to-buy', uz: 'qayerdan-sotib-olish' },
  'become-dealer': { ru: 'stat-dilerom', en: 'become-dealer', uz: 'diler-bolish' },
  useful: { ru: 'poleznoe', en: 'useful', uz: 'foydali' },
};

// ─── Useful hub slug map ──────────────────────────────────────────────────────
// Keys are canonical hub ids (matching useful-data.ts hub.id / hub.slug).

export const HUB_SLUG_MAP: Record<string, Record<Lang, string>> = {
  'spc-laminat': { ru: 'spc-laminat', en: 'spc-laminate', uz: 'spc-laminat' },
  'spc-paneli': { ru: 'spc-paneli', en: 'spc-panels', uz: 'spc-panellar' },
  'carpet-tile': { ru: 'kovrovaya-plitka', en: 'carpet-tile', uz: 'gilam-plitkasi' },
};

// ─── Useful article slug map ──────────────────────────────────────────────────
// Structure: articleSlugMap[hubId][articleId][lang] = localizedSlug
// Keys use canonical article ids (matching useful-data.ts article.id / article.slug).

export const ARTICLE_SLUG_MAP: Record<string, Record<string, Record<Lang, string>>> = {
  'spc-laminat': {
    'chto-takoe-spc': { ru: 'chto-takoe-spc', en: 'what-is-spc', uz: 'spc-nima' },
    'kupit-cena': { ru: 'kupit-cena', en: 'buy-price', uz: 'narx-sotib-olish' },
    'spc-vs-laminat': { ru: 'spc-ili-laminat', en: 'spc-vs-laminate', uz: 'spc-yoki-laminat' },
    'klass-tolshina-zashchitnyy-sloy': { ru: 'klass-tolshina-zashchitnyy-sloy', en: 'wear-class-thickness-wear-layer', uz: 'klass-qalinlik-himoya-qatlami' },
    'teplyy-pol-vannaya': { ru: 'teplyy-pol-vannaya', en: 'underfloor-heating-bathroom', uz: 'issiq-pol-vanna' },
    'podlozhka': { ru: 'podlozhka', en: 'underlay', uz: 'taglik' },
    'spc-caryapiny-pyatna': { ru: 'caryapiny-pyatna', en: 'scratches-stains', uz: 'tirnalishlar-doglar' },
    'plyusy-minusy-otzyvy': { ru: 'plyusy-minusy-otzyvy', en: 'pros-cons-reviews', uz: 'afzalliklar-kamchiliklar-fikrlar' },
  },
  'spc-paneli': {
    'kupit-cena': { ru: 'kupit-cena', en: 'buy-price', uz: 'narx-sotib-olish' },
    'chto-eto': { ru: 'chto-eto', en: 'what-is-it', uz: 'bu-nima' },
    'vannaya-kuhnya': { ru: 'vannaya-kuhnya', en: 'bathroom-kitchen', uz: 'vanna-oshxona' },
    'razmery-styki-ugly': { ru: 'razmery-styki-ugly', en: 'sizes-joints-corners', uz: 'olcham-birikmalar-burchaklar' },
    'spc-vs-pvh': { ru: 'spc-vs-pvh', en: 'spc-vs-pvc', uz: 'spc-vs-pvh' },
    'paneli-chistka': { ru: 'paneli-chistka', en: 'panel-cleaning', uz: 'panellar-tozalash' },
  },
  'carpet-tile': {
    'chto-eto': { ru: 'chto-eto', en: 'what-is-it', uz: 'bu-nima' },
    'kupit-cena-office': { ru: 'kupit-cena-ofis', en: 'buy-price-office', uz: 'narx-ofis' },
    'kommercheskaya-iznos': { ru: 'kommercheskaya-iznos', en: 'commercial-wear', uz: 'commercial-edish' },
    'uhod-chistka': { ru: 'uhod-chistka', en: 'care-cleaning', uz: 'parvarish-tozalash' },
  },
};

// ─── Reverse index maps (built lazily once) ───────────────────────────────────

/** slug → pageKey, per lang */
const _pageReverseMap: Partial<Record<Lang, Record<string, string>>> = {};

/** slug → hubId, per lang */
const _hubReverseMap: Partial<Record<Lang, Record<string, string>>> = {};

/** hubId → (slug → articleId), per lang */
const _articleReverseMap: Partial<Record<Lang, Record<string, Record<string, string>>>> = {};

function buildPageReverseMap(lang: Lang): Record<string, string> {
  if (_pageReverseMap[lang]) return _pageReverseMap[lang]!;
  const map: Record<string, string> = {};
  for (const [pageKey, slugs] of Object.entries(PAGE_SLUG_MAP)) {
    map[slugs[lang]] = pageKey;
  }
  _pageReverseMap[lang] = map;
  return map;
}

function buildHubReverseMap(lang: Lang): Record<string, string> {
  if (_hubReverseMap[lang]) return _hubReverseMap[lang]!;
  const map: Record<string, string> = {};
  for (const [hubId, slugs] of Object.entries(HUB_SLUG_MAP)) {
    map[slugs[lang]] = hubId;
  }
  _hubReverseMap[lang] = map;
  return map;
}

function buildArticleReverseMap(lang: Lang): Record<string, Record<string, string>> {
  if (_articleReverseMap[lang]) return _articleReverseMap[lang]!;
  const map: Record<string, Record<string, string>> = {};
  for (const [hubId, articles] of Object.entries(ARTICLE_SLUG_MAP)) {
    const hubSlug = HUB_SLUG_MAP[hubId]?.[lang] || hubId;
    map[hubSlug] = {};
    for (const [articleId, slugs] of Object.entries(articles)) {
      map[hubSlug][slugs[lang]] = articleId;
    }
  }
  _articleReverseMap[lang] = map;
  return map;
}

// ─── Page helpers ─────────────────────────────────────────────────────────────

/** Returns the localized slug for a page key in the given language. */
export function getPageSlug(pageKey: string, lang: Lang): string {
  return PAGE_SLUG_MAP[pageKey]?.[lang] ?? pageKey;
}

/** Returns the full path /{lang}/{localizedSlug} for a page key. */
export function getPagePath(pageKey: string, lang: Lang): string {
  return `/${lang}/${getPageSlug(pageKey, lang)}`;
}

/** Reverse: given a URL slug and lang, return the canonical page key (or null). */
export function resolvePageKeyFromSlug(slug: string, lang: Lang): string | null {
  return buildPageReverseMap(lang)[slug] ?? null;
}

// ─── Useful root helpers ──────────────────────────────────────────────────────

/** Returns the localized slug for the useful root (e.g. 'poleznoe' for ru). */
export function getUsefulRootSlug(lang: Lang): string {
  return PAGE_SLUG_MAP['useful'][lang];
}

/** Returns the full path to the useful root page /{lang}/{usefulSlug}. */
export function getUsefulRootPath(lang: Lang): string {
  return `/${lang}/${getUsefulRootSlug(lang)}`;
}

// ─── Hub helpers ──────────────────────────────────────────────────────────────

/** Returns the localized hub slug for a canonical hub id and lang. */
export function getUsefulHubSlug(hubId: string, lang: Lang): string {
  return HUB_SLUG_MAP[hubId]?.[lang] ?? hubId;
}

/** Returns the full path /{lang}/{usefulSlug}/{hubSlug}. */
export function buildUsefulHubPath(lang: Lang, hubId: string): string {
  return `/${lang}/${getUsefulRootSlug(lang)}/${getUsefulHubSlug(hubId, lang)}`;
}

/**
 * Reverse: given a localized hub slug from the URL and the current lang,
 * returns the canonical hub id (e.g. 'spc-paneli'), or null if not found.
 */
export function resolveHubIdFromSlug(hubSlug: string, lang: Lang): string | null {
  return buildHubReverseMap(lang)[hubSlug] ?? null;
}

// ─── Article helpers ──────────────────────────────────────────────────────────

/** Returns the localized article slug for a canonical hub id + article id + lang. */
export function getUsefulArticleSlug(hubId: string, articleId: string, lang: Lang): string {
  return ARTICLE_SLUG_MAP[hubId]?.[articleId]?.[lang] ?? articleId;
}

/** Returns the full path /{lang}/{usefulSlug}/{hubSlug}/{articleSlug}. */
export function buildUsefulArticlePath(lang: Lang, hubId: string, articleId: string): string {
  return `/${lang}/${getUsefulRootSlug(lang)}/${getUsefulHubSlug(hubId, lang)}/${getUsefulArticleSlug(hubId, articleId, lang)}`;
}

/**
 * Reverse: given a localized hub slug + localized article slug + lang,
 * returns the canonical article id, or null if not found.
 */
export function resolveArticleIdFromSlug(
  localizedHubSlug: string,
  localizedArticleSlug: string,
  lang: Lang,
): string | null {
  const map = buildArticleReverseMap(lang);
  return map[localizedHubSlug]?.[localizedArticleSlug] ?? null;
}

// ─── Legacy redirect helpers ──────────────────────────────────────────────────

/**
 * Given ANY incoming /:lang/SLUG path (possibly old/non-localized),
 * tries to find the correct localized path.
 *
 * Returns the new localized path if a redirect is needed, or null if
 * the path is already correct.
 *
 * Examples:
 *   resolveRedirect('/ru', 'about')           → '/ru/o-zavode'
 *   resolveRedirect('/en', 'about')           → null  (already correct)
 *   resolveRedirect('/ru', 'useful')          → '/ru/poleznoe'
 *   resolveRedirect('/en', 'useful')          → null  (already correct)
 *   resolveRedirect('/ru', 'spc-paneli', 'kupit-cena') → null
 *     (spc-paneli/kupit-cena are already the RU slugs)
 */
export function resolveIncomingPageSlug(lang: Lang, incomingSlug: string): string | null {
  // Is this slug already the correct localized slug for any page?
  const pageKey = resolvePageKeyFromSlug(incomingSlug, lang);
  if (pageKey) return null; // already correct

  // Is this one of the English canonical slugs that needs to be redirected?
  // Try all langs — if en slug matches for a different lang, redirect
  const allPageKeys = Object.keys(PAGE_SLUG_MAP);
  for (const key of allPageKeys) {
    for (const l of ['ru', 'en', 'uz'] as Lang[]) {
      if (PAGE_SLUG_MAP[key][l] === incomingSlug) {
        // This matches a slug in language `l`, but current lang is `lang`
        // Return the correct localized path for `lang`
        const correctSlug = PAGE_SLUG_MAP[key][lang];
        if (correctSlug !== incomingSlug) {
          return `/${lang}/${correctSlug}`;
        }
        return null;
      }
    }
  }
  return null;
}

/**
 * Given /:lang/usefulRootSlug/:hubSlug/:articleSlug path segments,
 * returns a redirect target if the hub or article slugs are wrong for the lang.
 * Returns null if already correct.
 */
export function resolveIncomingUsefulPath(
  lang: Lang,
  incomingHubSlug: string,
  incomingArticleSlug?: string,
): string | null {
  // First try to resolve hub id from incoming hub slug in this lang
  let hubId = resolveHubIdFromSlug(incomingHubSlug, lang);

  // If not found in current lang, try resolving from any lang
  if (!hubId) {
    for (const l of ['ru', 'en', 'uz'] as Lang[]) {
      const found = buildHubReverseMap(l)[incomingHubSlug];
      if (found) { hubId = found; break; }
    }
  }

  if (!hubId) return null; // unknown hub — can't redirect

  const correctHubSlug = getUsefulHubSlug(hubId, lang);
  const usefulRoot = getUsefulRootSlug(lang);

  if (!incomingArticleSlug) {
    // Hub only
    if (incomingHubSlug !== correctHubSlug) {
      return `/${lang}/${usefulRoot}/${correctHubSlug}`;
    }
    return null;
  }

  // Hub + article
  let articleId = resolveArticleIdFromSlug(incomingHubSlug, incomingArticleSlug, lang);

  // If not found in current lang, try resolving from any lang with this hub
  if (!articleId) {
    for (const l of ['ru', 'en', 'uz'] as Lang[]) {
      const hubSlugInLang = getUsefulHubSlug(hubId, l);
      const map = buildArticleReverseMap(l);
      const found = map[hubSlugInLang]?.[incomingArticleSlug];
      if (found) { articleId = found; break; }
    }
  }

  if (!articleId) return null; // unknown article

  const correctArticleSlug = getUsefulArticleSlug(hubId, articleId, lang);

  if (incomingHubSlug !== correctHubSlug || incomingArticleSlug !== correctArticleSlug) {
    return `/${lang}/${usefulRoot}/${correctHubSlug}/${correctArticleSlug}`;
  }

  return null;
}

// ─── Canonical hub id array (ordered) ────────────────────────────────────────
// For swipe navigation — defines the canonical order of hubs.

export const HUB_IDS_ORDERED = ['spc-laminat', 'spc-paneli', 'carpet-tile'] as const;
export type HubId = (typeof HUB_IDS_ORDERED)[number];
